import React, { useState, useEffect } from 'react';
import { Plus, Building2, Globe, Shield, Users, Trash2, Edit, ToggleLeft, ToggleRight, AlertCircle, CheckCircle, Info } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import Header from '../../components/layout/Header';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE } from '../../config/api';

const API_URL = API_BASE;

export default function EnterpriseTenantAdmin() {
  const { token } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [newTenant, setNewTenant] = useState({
    company_name: '',
    allowed_domain: '',
    entra_tenant_id: '',
    is_active: true
  });

  // Fetch tenants
  const fetchTenants = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v1/admin/tenants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenants(response.data.tenants || []);
    } catch (err) {
      console.error('Failed to fetch tenants:', err);
      toast.error('Failed to load enterprise tenants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, [token]);

  // Create tenant
  const handleCreateTenant = async (e) => {
    e.preventDefault();
    setCreateLoading(true);

    try {
      // Validate UUID format for Entra Tenant ID
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(newTenant.entra_tenant_id)) {
        toast.error('Invalid Entra Tenant ID format. Must be a valid UUID.');
        setCreateLoading(false);
        return;
      }

      await axios.post(`${API_URL}/api/v1/admin/tenants`, newTenant, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Enterprise tenant "${newTenant.company_name}" created successfully`);
      setShowCreateModal(false);
      setNewTenant({ company_name: '', allowed_domain: '', entra_tenant_id: '', is_active: true });
      fetchTenants();
    } catch (err) {
      console.error('Failed to create tenant:', err);
      toast.error(err.response?.data?.detail || 'Failed to create tenant');
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navigate-dark">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Building2 className="h-7 w-7 text-blue-400" />
              Enterprise Tenants
            </h1>
            <p className="text-zinc-400 mt-1">
              Manage Microsoft Entra ID SSO configurations for enterprise clients
            </p>
          </div>
          
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            data-testid="create-tenant-btn"
          >
            <Plus className="h-4 w-4" />
            Add Enterprise Tenant
          </button>
        </div>

        {/* Configuration Status Banner */}
        <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
            <div>
              <h3 className="font-medium text-amber-400 mb-1">Microsoft Entra ID Configuration Required</h3>
              <p className="text-sm text-amber-300/80">
                To enable SSO for enterprise clients, configure the following environment variables:
              </p>
              <ul className="text-sm text-amber-300/80 mt-2 space-y-1">
                <li>• <code className="bg-amber-900/30 px-1 rounded">AZURE_AD_CLIENT_ID</code> - Your Azure App Registration Client ID</li>
                <li>• <code className="bg-amber-900/30 px-1 rounded">AZURE_AD_CLIENT_SECRET</code> - Your Azure App Client Secret</li>
                <li>• <code className="bg-amber-900/30 px-1 rounded">AZURE_AD_REDIRECT_URI</code> - SSO callback URL</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Tenants Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
          </div>
        ) : tenants.length === 0 ? (
          <div className="text-center py-12 bg-navigate-surface rounded-lg border border-zinc-800">
            <Building2 className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No Enterprise Tenants</h3>
            <p className="text-zinc-400 mb-4">
              Create your first enterprise tenant to enable SSO for client organizations.
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <Plus className="h-4 w-4" />
              Add Enterprise Tenant
            </button>
          </div>
        ) : (
          <div className="bg-navigate-surface rounded-lg border border-zinc-800 overflow-hidden">
            <table className="w-full">
              <thead className="bg-zinc-800/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-zinc-300">Company</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-zinc-300">Domain</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-zinc-300">Entra Tenant ID</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-zinc-300">Users</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-zinc-300">Status</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-zinc-300">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {tenants.map((tenant) => (
                  <tr key={tenant.tenant_id} className="hover:bg-zinc-800/30 transition-colors">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 bg-blue-600/20 rounded-lg flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-blue-400" />
                        </div>
                        <div>
                          <p className="font-medium text-white">{tenant.company_name}</p>
                          <p className="text-xs text-zinc-500">{tenant.tenant_id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2 text-zinc-300">
                        <Globe className="h-4 w-4 text-zinc-500" />
                        {tenant.allowed_domain}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <code className="text-xs text-zinc-400 bg-zinc-800 px-2 py-1 rounded">
                        {tenant.entra_tenant_id}
                      </code>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="flex items-center justify-center gap-1 text-zinc-300">
                        <Users className="h-4 w-4 text-zinc-500" />
                        {tenant.user_count || 0}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      {tenant.is_active ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/10 text-green-400 rounded text-xs">
                          <CheckCircle className="h-3 w-3" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-500/10 text-red-400 rounded text-xs">
                          <AlertCircle className="h-3 w-3" />
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right text-sm text-zinc-400">
                      {new Date(tenant.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create Tenant Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-navigate-surface rounded-lg border border-zinc-800 w-full max-w-lg">
              <div className="p-6 border-b border-zinc-800">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-blue-400" />
                  Add Enterprise Tenant
                </h2>
                <p className="text-sm text-zinc-400 mt-1">
                  Configure SSO for a new enterprise client
                </p>
              </div>
              
              <form onSubmit={handleCreateTenant} className="p-6 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name" className="text-zinc-300">Company Name *</Label>
                  <Input
                    id="company_name"
                    value={newTenant.company_name}
                    onChange={(e) => setNewTenant({ ...newTenant, company_name: e.target.value })}
                    placeholder="Acme Corporation"
                    required
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="tenant-company-name"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="allowed_domain" className="text-zinc-300">Email Domain *</Label>
                  <Input
                    id="allowed_domain"
                    value={newTenant.allowed_domain}
                    onChange={(e) => setNewTenant({ ...newTenant, allowed_domain: e.target.value.toLowerCase() })}
                    placeholder="acme.com"
                    required
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="tenant-domain"
                  />
                  <p className="text-xs text-zinc-500">Users with @{newTenant.allowed_domain || 'domain.com'} emails will be able to SSO</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="entra_tenant_id" className="text-zinc-300">Microsoft Entra Tenant ID *</Label>
                  <Input
                    id="entra_tenant_id"
                    value={newTenant.entra_tenant_id}
                    onChange={(e) => setNewTenant({ ...newTenant, entra_tenant_id: e.target.value })}
                    placeholder="00000000-0000-0000-0000-000000000000"
                    required
                    className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm"
                    data-testid="tenant-entra-id"
                  />
                  <p className="text-xs text-zinc-500">
                    Found in Azure Portal → Azure Active Directory → Overview → Tenant ID
                  </p>
                </div>
                
                <div className="flex items-center gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setNewTenant({ ...newTenant, is_active: !newTenant.is_active })}
                    className="flex items-center gap-2 text-sm"
                  >
                    {newTenant.is_active ? (
                      <ToggleRight className="h-6 w-6 text-green-400" />
                    ) : (
                      <ToggleLeft className="h-6 w-6 text-zinc-500" />
                    )}
                    <span className={newTenant.is_active ? 'text-green-400' : 'text-zinc-400'}>
                      {newTenant.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </button>
                </div>
                
                <div className="flex gap-3 pt-4 border-t border-zinc-800">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createLoading}
                    className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
                    data-testid="submit-create-tenant"
                  >
                    {createLoading ? 'Creating...' : 'Create Tenant'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
